import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { auth, db } from '../../firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import UpgradeModal from '../Shared/UpgradeModal';

export type PlanType = 'free' | 'premium';

export interface SubscriptionLimits {
    maxClasses: number;
    maxStudents: number;
    maxRubrics: number;
    maxQuizzes: number;
    maxQuizQuestions: number;
    maxActiveGames: number;
    maxActiveForms: number;
    maxActiveGroups: number;
    canExport: boolean;
    canUseKiosk: boolean;
    maxAiImportsTotal: number;
    maxAiImportsPerMonth: number;
    maxObservations: number;
    maxConversations: number;
}

export interface FeatureAccess {
    canUseALP: boolean;
    canExportRubrics: boolean;
    canUseCustomTemplates: boolean;
    canExport: boolean;
    canUseKiosk: boolean;
}

const FREE_LIMITS: SubscriptionLimits = {
    maxClasses: 1,
    maxStudents: 30,
    maxRubrics: 1,
    maxQuizzes: 3,
    maxQuizQuestions: 5,
    maxActiveGames: 5,
    maxActiveForms: 1, // [NEW] Limit 1 active form
    maxActiveGroups: 3, // [NEW] Limit 3 active groups
    canExport: false, // [NEW] Block all exports
    canUseKiosk: false, // [NEW] Block Kiosk mode
    maxAiImportsTotal: 2,
    maxAiImportsPerMonth: 0,
    maxObservations: 1,
    maxConversations: 1,
};

const PREMIUM_LIMITS: SubscriptionLimits = {
    maxClasses: 9999,
    maxStudents: 9999,
    maxRubrics: 9999,
    maxQuizzes: 9999,
    maxQuizQuestions: 9999,
    maxActiveGames: 9999,
    maxActiveForms: 9999,
    maxActiveGroups: 9999,
    canExport: true,
    canUseKiosk: true,
    maxAiImportsTotal: 9999,
    maxAiImportsPerMonth: 50,
    maxObservations: 999,
    maxConversations: 999,
};

interface SubscriptionContextType {
    plan: PlanType;
    loading: boolean;
    limits: SubscriptionLimits;
    features: FeatureAccess;
    checkLimit: (feature: keyof SubscriptionLimits, currentCount: number) => boolean;
    isOverLimit: (feature: keyof SubscriptionLimits, index: number) => boolean;
    isUpgradeModalOpen: boolean;
    setIsUpgradeModalOpen: (isOpen: boolean) => void;
    checkAiImportLimit: () => boolean;
    incrementAiImport: () => Promise<void>;
    aiImportsCountTotal: number;
    aiImportsCountMonth: number;
    hasUsedTrial: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(auth.currentUser);
    const [plan, setPlan] = useState<PlanType>('free'); // Default to free
    const [loading, setLoading] = useState(true);

    const [aiImportsCountTotal, setAiImportsCountTotal] = useState(0);
    const [aiImportsCountMonth, setAiImportsCountMonth] = useState(0);
    const [aiImportsMonth, setAiImportsMonth] = useState('');
    const [hasUsedTrial, setHasUsedTrial] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            if (!currentUser) {
                setPlan('free');
                setHasUsedTrial(false);
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!user) return;

        // Listen for subscription changes
        const unsub = onSnapshot(doc(db, 'users', user.uid), (docSnapshot) => {
            const data = docSnapshot.data();
            if (data?.subscriptionStatus === 'active' || data?.subscriptionStatus === 'trialing') {
                setPlan('premium');
            } else {
                setPlan('free');
            }

            setAiImportsCountTotal(data?.aiImportsCountTotal || 0);
            setAiImportsCountMonth(data?.aiImportsCountMonth || 0);
            setAiImportsMonth(data?.aiImportsMonth || '');
            setHasUsedTrial(data?.hasUsedTrial || false);

            setLoading(false);
        }, (err) => {
            console.error("Subscription listener error:", err);
            setPlan('free');
            setLoading(false);
        });

        return () => unsub();
    }, [user]);

    const activeLimits = plan === 'premium' ? PREMIUM_LIMITS : FREE_LIMITS;

    const features: FeatureAccess = {
        canUseALP: plan === 'premium',
        canExportRubrics: plan === 'premium',
        canUseCustomTemplates: plan === 'premium',
        canExport: plan === 'premium',
        canUseKiosk: plan === 'premium',
    };

    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

    const checkLimit = (feature: keyof SubscriptionLimits, currentCount: number): boolean => {
        if (plan === 'premium') return true;
        const limit = activeLimits[feature];
        if (typeof limit === 'boolean') return limit;
        return currentCount < limit;
    };

    const isOverLimit = (feature: keyof SubscriptionLimits, index: number): boolean => {
        if (plan === 'premium') return false;
        const limit = activeLimits[feature];
        if (typeof limit === 'boolean') return !limit;
        return index >= limit;
    };

    const checkAiImportLimit = (): boolean => {
        if (plan === 'free') {
            return aiImportsCountTotal < activeLimits.maxAiImportsTotal;
        } else {
            const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
            if (aiImportsMonth !== currentMonth) return true; // New month
            return aiImportsCountMonth < activeLimits.maxAiImportsPerMonth;
        }
    };

    const incrementAiImport = async () => {
        if (!user) return;
        
        const currentMonth = new Date().toISOString().slice(0, 7);
        let newCountMonth = aiImportsCountMonth + 1;

        if (aiImportsMonth !== currentMonth) {
            newCountMonth = 1;
        }

        const newCountTotal = aiImportsCountTotal + 1;
        
        // Optimistic update
        setAiImportsCountTotal(newCountTotal);
        setAiImportsCountMonth(newCountMonth);
        setAiImportsMonth(currentMonth);
    };

    return (
        <SubscriptionContext.Provider value={{
            plan,
            loading,
            limits: activeLimits,
            features,
            checkLimit,
            isOverLimit,
            isUpgradeModalOpen,
            setIsUpgradeModalOpen,
            checkAiImportLimit,
            incrementAiImport,
            aiImportsCountTotal,
            aiImportsCountMonth,
            hasUsedTrial
        }}>
            {children}
            {isUpgradeModalOpen && <UpgradeModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} />}
        </SubscriptionContext.Provider>
    );
};

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
};
